import { LevelActionType } from '~/bootcamp/types/bootcamp.types'

import { CurriculumGradeLevelType } from '../constants/curriculum.const'

import { SubtopicContent } from './curriculum.types'

export interface SanityObjectReference {
  _ref: string
  _type: string
}

export interface CurriculumMappingQuizDescriptionChildrenDto {
  marks: []
  text: string
  _key: string
  _type: string
}

export interface CurriculumMappingQuizQuestionDescriptionDto {
  _key: string
  _type: string
  style: string
  markDefs: []
  children: CurriculumMappingQuizDescriptionChildrenDto[]
}

export interface CurriculumMappingQuizQuestionResponseDto {
  questionId: string
  quizId: string
  title: string
  description: CurriculumMappingQuizQuestionDescriptionDto[]
  preAskedQuesIds: string[]
  isLastQuestion: boolean
  isQuizComplete: boolean
  tasks: CurriculumMappingQuizTask
}

export interface GradeInfo {
  countryCode: string
  gradeId: string
  gradeLevel: number
  subjectOrder: number
  subject: string
  topicId: string
  version?: string
  topicUUID?: string
}
export interface LastLevelStatusInfo {
  isLastLevel: boolean
  lastLevelIds: string[]
  gradeInfo: GradeInfo
}

export interface Level {
  levelIds: string[]
  title: string
  path: string
  isCompleted: boolean
  iconType: LevelActionType
  type: CurriculumGradeLevelType
}

export interface SubjectListItem {
  title: string
  slug: string
  levels: Level[]
  isCompleted: boolean
}

export interface Subject {
  subject: string
  subjectList: SubjectListItem[]
}

export interface MindLevel {
  title: string
  path: string
  levelIds: string[]
  isCompleted: boolean
  type: LevelActionType
  iconType: LevelActionType
}

/**
 * A must-knows level as served by `/v1/curriculum/quiz-task` (v2 lean shape, `MustKnowsViewV2`).
 * A frontend-consumed subset of hom-api `curriculum.type.ts:MustKnowsLevelView` — the wire response
 * also carries `basePath` and `levelIds`, but the client never reads them, so they are omitted here.
 * `levelId` is the curriculumContent doc id (level identity); `slug` is the resolved routing slug.
 * `content` (the full Sanity curriculumContent doc) is inlined ONLY when the request is scoped to a
 * `subTopicSlug`; it is omitted for a whole-topic (overview) request, and is `null` when a scoped
 * level resolves to nothing.
 */
export interface MustKnowsLevelView {
  levelId: string
  title: string
  slug?: string
  content?: SubtopicContent | null
}

/**
 * Narrows a must-know level to one whose full content is inlined (scoped sub-topic request).
 * Only such a level can be opened/rendered; a lightweight or resolves-to-nothing level cannot.
 */
export const isFullLevelContent = (
  level: MustKnowsLevelView,
): level is MustKnowsLevelView & { content: SubtopicContent } => level.content != null

/**
 * Narrows a must-know level to one the sub-topic page can open AND route: full inlined content
 * plus a non-empty routing slug. Legacy slim rows keep `slug: ''` until the mapping quiz is
 * retaken (the API backfills it from the content doc when it can) — routing a slug-less level
 * would build an unresolvable URL and ping-pong between auto-select and the invalid-slug cleanup
 * (visible as the sub-topic page flickering).
 */
export const isOpenableMustKnowLevel = (
  level: MustKnowsLevelView,
): level is MustKnowsLevelView & { content: SubtopicContent; slug: string } => isFullLevelContent(level) && !!level.slug

/** A must-knows block (mirrors hom-api `MustKnowsEntryView`). `isCompleted` is left `false` by the
 * API's slim builder — do NOT trust it; derive completion client-side from progress (see
 * `isMustKnowLevelCompleteFromProgress`, keyed by each level's `levelId`). */
export interface MindBlocks {
  title: string
  isCompleted: boolean
  slug?: string
  levels: MustKnowsLevelView[]
}

export interface CurriculumMappingQuizTask {
  [subTopic: string]: MindBlocks[]
}
export interface CurriculumMappingQuizTaskDto {
  isQuizComplete: boolean
  isQuizExist: boolean
  tasks: CurriculumMappingQuizTask
}

export interface CurriculumMappingQuizQuestion {
  _id: string
  questionId: string
  quizId: string
  slug: string
  title: string
  description: string
  yesPath: SanityObjectReference
  noPath: SanityObjectReference
  haveYesPathCondition: boolean
  yesConditions: {
    _type: string
    questions: Array<{
      expectedAnswer: string
      curriculumMappingQuizQuestion: {
        _ref: string
        _type: string
      }
    }>
    operator: 'OR' | 'AND'
  }
  haveNoPathCondition: boolean
  noConditions: {
    _type: string
    questions: Array<{
      expectedAnswer: string
      curriculumMappingQuizQuestion: {
        _ref: string
        _type: string
      }
    }>
    operator: 'OR' | 'AND'
  }
  noTaskCondition: boolean
  tasksOnNo: {
    bootcampModule: SanityObjectReference[]
    bootcampPage: SanityObjectReference[]
  }
}
